/**
 * In-session draft persistence for the "New Conversation" builder.
 *
 * The builder (`CreateInterviewForm`) keeps all of its in-progress state local,
 * so navigating away (tapping the settings cog, say) unmounts it and drops the
 * work. This module snapshots the authored content to `sessionStorage` so the
 * build survives an in-app navigation OR a reload within the same tab (a
 * dropped connection). It is deliberately NOT durable: the draft lives only for
 * the tab session and is cleared the moment the conversation is created or the
 * user starts a fresh one. Database-backed auto-save is a separate, future
 * concern.
 *
 * Scope note: this snapshot does not preserve uploaded information-block
 * images. Those are cleaned up on unmount/unload by
 * `useInformationBlockAbandonCleanup`, so a restored block may need its image
 * re-added. That trade-off is accepted for this transient safety net.
 *
 * @module components/setupDraft
 */

import type { FormField } from '../types';
import type { SetupStep } from './CreateInterviewForm';
import { isFormFieldDraft } from '../lib/form-capabilities';
import type { ConversationExperienceConfig } from '../lib/multimodal-contract';
import { parseFormImportReport, type FormImportReport } from '../lib/form-import-report';

/**
 * The authored builder state captured in a draft. Mirrors the user-editable
 * state in `CreateInterviewForm`; transient process flags (generation status,
 * slide-over open state) are intentionally excluded.
 */
export interface SetupDraft {
  /** Import notes belong to this draft and workspace, never to respondent fields. */
  importReport?: { workspaceId: string; report: FormImportReport };
  /** Versioned settings; the wizard normalizes older drafts into the unified editor. */
  conversationConfig?: ConversationExperienceConfig | null;
  /** Wizard step the user was on (objectives vs builder canvas). */
  step: SetupStep;
  /** Inline form title (saved as `target_name`). */
  formTitle: string;
  /** Conversation goal that steers holito. */
  objective: string;
  /** Free-text objective typed into the composer on the objectives step. */
  freeFormObjective: string;
  /** Output language code. */
  outputLanguage: string;
  /** Whether video capture is enabled. */
  videoEnabled: boolean;
  /** Whether screen sharing is enabled. */
  screenShareEnabled: boolean;
  /** Response cap, or null for unlimited. */
  maxResponses: number | null;
  /** Whether duplicate respondent emails are allowed. */
  allowDuplicateEmails: boolean;
  /** Whether the respondent name is required. */
  requireName: boolean;
  /** Whether the respondent email is required. */
  requireEmail: boolean;
  /** Whether a video response is required. */
  requireVideo: boolean;
  /** Forced output language, or null when the respondent may choose. */
  enforceLanguage: string | null;
  /**
   * When identity is collected ('pre' | 'post' | 'never'). Optional: absent
   * in drafts saved before the settings-parity work; treated as 'pre'.
   */
  identityCollection?: 'pre' | 'post' | 'never';
  /** Post-call behaviour. Optional (older drafts); treated as 'message'. */
  completionAction?: 'message' | 'redirect';
  /** Custom success-screen body, or null for the default copy. */
  completionMessage?: string | null;
  /** Destination URL when completionAction is 'redirect'. */
  completionRedirectUrl?: string | null;
  /** ISO open timestamp, or null for immediately open. */
  opensAt?: string | null;
  /** ISO close timestamp, or null for no auto-close. */
  closesAt?: string | null;
  /** Authored question set on the builder canvas. */
  fields: FormField[];
}

/**
 * Versioned storage key. Bumping the suffix invalidates older drafts whose
 * shape no longer matches {@link SetupDraft}.
 */
const STORAGE_KEY = 'holito.setup-draft.v1';

/**
 * Read the current in-session draft.
 *
 * @returns The stored draft, or null when absent, unparseable, or storage is
 *   unavailable (private mode). A malformed payload is treated as "no draft".
 */
export function loadSetupDraft(): SetupDraft | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SetupDraft;
    // Minimal shape guard: a usable draft must carry a fields array. Anything
    // else is stale/corrupt and is ignored rather than rehydrated.
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.fields)) {
      return null;
    }
    if (!parsed.fields.every(isFormFieldDraft)) return null;
    const imported = parsed.importReport;
    delete parsed.importReport;
    if (parsed.step === 'builder' && imported && typeof imported === 'object'
      && Object.keys(imported).every(key => key === 'workspaceId' || key === 'report')
      && typeof imported.workspaceId === 'string' && /^[A-Za-z0-9_-]{1,200}$/.test(imported.workspaceId)) {
      const report = parseFormImportReport(imported.report);
      if (report) parsed.importReport = { workspaceId: imported.workspaceId, report };
    }
    return parsed;
  } catch {
    // Corrupt JSON or a SecurityError reading storage: fall back to no draft.
    return null;
  }
}

/**
 * Persist the in-session draft, overwriting any previous snapshot.
 *
 * @param draft The authored builder state to store.
 */
export function saveSetupDraft(draft: SetupDraft): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Quota exceeded or storage unavailable: losing the safety net is
    // non-fatal, so swallow rather than crash the builder.
  }
}

/** Remove the in-session draft (on create success or an explicit fresh start). */
export function clearSetupDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Non-fatal: see saveSetupDraft.
  }
}
